import React, { useState, useEffect, useRef } from 'react';
import { useTelegram } from '../hooks/useTelegram';
import { useMenuHeight } from '../hooks/useMenuHeight';
import { ReferralListSheet } from './ReferralListSheet';
import { Notification } from './Notification';
import { QRCodeSheet } from './QRCodeSheet';
import { WithdrawalSheet } from './WithdrawalSheet';
import '../App.css';
import {Portal} from "./portal";

const ASSET_PATH = '/images';
const API_URL = window.location.origin;

export function ReferralPage({ onPageLoaded }) {
    const [referralLink, setReferralLink] = useState('');
    const [referralLinks, setReferralLinks] = useState({ telegram: '', web: '' });
    const [stats, setStats] = useState({
        friends: 0,
        monthlyEarnings: 0,
        monthlyChange: 0,
        yearlyEarnings: 0,
        yearlyChange: 0,
        totalEarnings: 0,
        availableBalance: 0,
        percentage: 15,
        minWithdrawal: 0 // ← Устанавливаем в 0, чтобы не блокировать вывод
    });

    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReferralList, setShowReferralList] = useState(false);
    const [showQRCode, setShowQRCode] = useState(false);
    const [showWithdrawalSheet, setShowWithdrawalSheet] = useState(false);
    const [notification, setNotification] = useState({
        isVisible: false,
        message: '',
        type: 'success'
    });
    const [linkType, setLinkType] = useState('tg');
    const { userId, telegramId, username, loading: tgLoading } = useTelegram();

    const menuHeight = useMenuHeight();

    // Refs для анимации переключения
    const tabContainerRef = useRef(null);
    const tabRefs = {
        tg: useRef(null),
        web: useRef(null)
    };
    const [tabHighlightStyle, setTabHighlightStyle] = useState({ left: 0, width: 0 });

    // Загрузка данных пользователя
    useEffect(() => {
        console.log('🔄 ReferralPage: useEffect triggered, userId:', userId, 'tgLoading:', tgLoading);

        const timeoutId = setTimeout(() => {
            console.log('⚠️ ReferralPage: Таймаут загрузки, принудительно завершаем');
            setLoading(false);
            if (onPageLoaded) {
                onPageLoaded();
            }
        }, 5000);

        if (userId && !tgLoading) {
            console.log('📡 ReferralPage: Начинаем загрузку данных...');

            Promise.all([
                fetchReferralData(),
                fetchReferralLink()
            ]).then(() => {
                console.log('✅ ReferralPage: Все данные загружены');
                setLoading(false);
                clearTimeout(timeoutId);
                if (onPageLoaded) {
                    console.log('📢 ReferralPage: Вызываем onPageLoaded');
                    onPageLoaded();
                }
            }).catch((error) => {
                console.error('❌ ReferralPage: Ошибка загрузки данных:', error);
                setLoading(false);
                clearTimeout(timeoutId);
                if (onPageLoaded) {
                    onPageLoaded();
                }
            });
        } else {
            console.log('⏳ ReferralPage: Ожидание userId или tgLoading...');
        }

        return () => clearTimeout(timeoutId);
    }, [userId, tgLoading]);

    // Генерация ссылки при смене типа
    useEffect(() => {
        if (userId && !loading) {
            generateReferralLink();
        }
    }, [linkType, userId, loading, referralLinks.telegram, referralLinks.web]);

    // Обновление позиции хайлайта
    useEffect(() => {
        if (!loading && tabContainerRef.current && tabRefs[linkType]?.current) {
            const containerRect = tabContainerRef.current.getBoundingClientRect();
            const activeRect = tabRefs[linkType].current.getBoundingClientRect();
            setTabHighlightStyle({
                left: activeRect.left - containerRect.left,
                width: activeRect.width
            });
        }
    }, [linkType, loading]);

    // Установка начальной позиции хайлайта
    useEffect(() => {
        if (!loading && tabContainerRef.current && tabRefs.tg?.current) {
            const containerRect = tabContainerRef.current.getBoundingClientRect();
            const activeRect = tabRefs.tg.current.getBoundingClientRect();
            setTabHighlightStyle({
                left: activeRect.left - containerRect.left,
                width: activeRect.width
            });
        }
    }, [loading]);

    // После получения данных из API
    const fetchReferralData = async () => {
        try {
            console.log('📊 ReferralPage: Запрос реферальных данных...');
            const response = await fetch(`${API_URL}/api/user/${userId}/referrals`);
            const data = await response.json();
            if (data.success) {
                setReferrals(data.referrals || []);

                // Преобразуем все числовые значения в числа
                const numericStats = {
                    friends: Number(data.stats.friends) || 0,
                    monthlyEarnings: Number(data.stats.monthlyEarnings) || 0,
                    monthlyChange: Number(data.stats.monthlyChange) || 0,
                    yearlyEarnings: Number(data.stats.yearlyEarnings) || 0,
                    yearlyChange: Number(data.stats.yearlyChange) || 0,
                    totalEarnings: Number(data.stats.totalEarnings) || 0,
                    availableBalance: Number(data.stats.availableBalance) || 0,
                    percentage: Number(data.stats.percentage) || 15,
                    minWithdrawal: Number(data.stats.minWithdrawal) || 0
                };

                setStats(numericStats);
                console.log('📊 ReferralPage: Рефералов получено:', data.referrals?.length || 0);
                console.log('📊 ReferralPage: Статистика:', numericStats);
            }
        } catch (error) {
            console.error('❌ Ошибка загрузки реферальных данных:', error);
            throw error;
        }
    };

    const fetchReferralLink = async () => {
        try {
            console.log('🔗 ReferralPage: Запрос реферальной ссылки...');
            const response = await fetch(`${API_URL}/api/user/${userId}/referral-link`);
            const data = await response.json();
            if (data.success) {
                const links = {
                    telegram: data.telegramLink || data.referralLink || '',
                    web: data.webReferralLink || ''
                };
                setReferralLinks(links);
                setReferralLink(linkType === 'web' ? links.web : links.telegram);
                console.log('🔗 ReferralPage: Защищённые реферальные ссылки получены');
            }
        } catch (error) {
            console.error('❌ Ошибка загрузки реферальной ссылки:', error);
            throw error;
        }
    };

    const generateReferralLink = () => {
        const newLink = linkType === 'web' ? referralLinks.web : referralLinks.telegram;
        if (newLink) setReferralLink(newLink);
    };

    const handleLinkTypeChange = (type) => {
        console.log('🔄 ReferralPage: Смена типа ссылки на:', type);
        setLinkType(type);
    };

    const handleCopyLink = () => {
        if (!referralLink) {
            showNotification('Ссылка еще не сгенерирована', 'error');
            return;
        }

        navigator.clipboard.writeText(referralLink).then(() => {
            showNotification('Ссылка скопирована!', 'success');
        }).catch((error) => {
            console.error('Ошибка копирования:', error);
            showNotification('Ошибка при копировании', 'error');
        });
    };

    const handleShare = () => {
        if (!referralLink) {
            showNotification('Ссылка еще не сгенерирована', 'error');
            return;
        }

        if (navigator.share) {
            navigator.share({
                title: 'Приглашение в HydraVPN',
                text: 'Присоединяйся к HydraVPN по моей реферальной ссылке!',
                url: referralLink
            }).catch(console.error);
        } else {
            handleCopyLink();
        }
    };

    const handleQR = () => {
        if (!referralLink) {
            showNotification('Ссылка еще не сгенерирована', 'error');
            return;
        }
        setShowQRCode(true);
    };

    const handleCloseQR = () => {
        setShowQRCode(false);
    };

    // Исправленная функция handleWithdrawal
    const handleWithdrawal = () => {
        console.log('💰 handleWithdrawal вызван');
        console.log('💰 availableBalance:', stats.availableBalance);
        console.log('💰 type of availableBalance:', typeof stats.availableBalance);
        console.log('💰 minWithdrawal:', stats.minWithdrawal);

        // Проверяем, есть ли средства для вывода
        const balance = Number(stats.availableBalance) || 0;

        // if (balance <= 0) {
        //     showNotification('На реферальном балансе нет средств для вывода', 'warning');
        //     return;
        // }

        console.log('💰 Открываем шторку вывода');
        setShowWithdrawalSheet(true);
    };

    // Обработка выбора способа вывода
    const handleWithdrawalConfirm = async (method, withdrawalAmount) => {
        setShowWithdrawalSheet(false);

        try {
            const response = await fetch(`${API_URL}/api/referral/withdraw`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: userId,
                    amount: withdrawalAmount,
                    method: method,
                    telegramId: telegramId,
                    username: username
                })
            });

            const data = await response.json();

            if (data.success) {
                if (method === 'balance') {
                    showNotification(`✅ ${data.message}`, 'success');
                    fetchReferralData();
                } else {
                    showNotification(`✅ Заявка на вывод отправлена! Ожидайте обработки.`, 'success');
                }
            } else {
                showNotification(`${data.message}`, 'error');
            }
        } catch (error) {
            console.error('Ошибка при выводе средств:', error);
            showNotification('❌ Ошибка при выводе средств', 'error');
        }
    };

    const showNotification = (message, type = 'success') => {
        setNotification({ isVisible: true, message, type });
        setTimeout(() => {
            setNotification(prev => ({ ...prev, isVisible: false }));
        }, 3000);
    };

    const hideNotification = () => {
        setNotification(prev => ({ ...prev, isVisible: false }));
    };

    const REF_PERC = process.env.REFERRAL_PERCENT || 25;

    // Функция для форматирования изменения в процентах
    const formatChange = (current, previous) => {
        // Преобразуем в числа
        const currentNum = Number(current) || 0;
        const previousNum = Number(previous) || 0;

        if (previousNum === 0) {
            return currentNum > 0 ? '+100%' : '0%';
        }
        const change = ((currentNum - previousNum) / previousNum) * 100;
        const sign = change > 0 ? '+' : '';
        return `${sign}${change.toFixed(1)}%`;
    };

    console.log('🎨 ReferralPage: Рендер, loading =', loading);
    console.log('📏 ReferralPage: Высота меню =', menuHeight);

    if (loading) {
        console.log('⏳ ReferralPage: Показываем null (загрузка)');
        return null;
    }

    if (!userId) {
        console.log('❌ ReferralPage: Нет userId');
        return (
            <div className="referral-page">
                <img
                    src={`${ASSET_PATH}/0_43.svg`}
                    className="referral-background"
                    alt="Background Pattern"
                />
                <div className="referral-content">
                    <div className="referral-card" style={{ padding: '32px', textAlign: 'center' }}>
                        <h2 style={{ color: 'white', marginBottom: '16px' }}>Ошибка авторизации</h2>
                        <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Не удалось получить данные пользователя
                        </p>
                    </div>
                </div>
            </div>
        );
    }
    const platform = window.Telegram?.WebApp?.platform;
    const isWindows = platform === 'windows' || platform === 'tdesktop' || platform === 'web';
    console.log('✅ ReferralPage: Показываем контент');
    return (
        <div className="referral-page page-enter-animate">
            {/* СКРОЛЛ-КОНТЕЙНЕР ТОЛЬКО ДЛЯ КОНТЕНТА */}
            <div className="referral-content">
                <div className="referral-card">
                    <div className="referral-header">
                        <h2 className="referral-title">Реферальная программа</h2>
                    </div>

                    <div
                        className="friends-counter"
                        onClick={() => setShowReferralList(true)}
                    >
                        <span className="friends-count">{stats.friends}</span>
                        <span className="friends-label">друзей</span>
                    </div>

                    {/* Статистика за месяц */}
                    <div className="stats-grid">
                        <div className="stat-item">
                            <span className="stat-value">{stats.monthlyEarnings.toFixed(2)} ₽</span>
                            <span className="stat-label">За месяц</span>
                            {stats.monthlyChange > 0 && (
                                <span className="stat-change positive">
                                ↑ {formatChange(stats.monthlyEarnings, stats.monthlyChange)}
                            </span>
                            )}
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <span className="stat-value">{stats.yearlyEarnings.toFixed(2)} ₽</span>
                            <span className="stat-label">За год</span>
                            {stats.yearlyChange > 0 && (
                                <span className="stat-change positive">
                                ↑ {formatChange(stats.yearlyEarnings, stats.yearlyChange)}
                            </span>
                            )}
                        </div>
                    </div>

                    {/* Доступный баланс */}
                    <div className="stats-grid">
                        <div className="stat-item">
                            <p className="stat-value">Реферальный баланс:</p>
                            <p className="stat-label">{stats.availableBalance} ₽</p>
                        </div>
                    </div>

                    <div className="percentage-info">
                        <p className="percentage-text">
                            Ваш процент: {REF_PERC}%
                        </p>
                    </div>

                    {/* Кнопка вывода */}
                    <div className="percentage-info">
                        <button
                            className="btn btn-green active"
                            onClick={handleWithdrawal}
                        >
                            <span className="withdrawal-text">Вывод</span>
                        </button>
                        {stats.availableBalance <= 0 && (
                            <p style={{fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px'}}>
                                Нет средств для вывода
                            </p>
                        )}
                    </div>

                    <div className="referral-divider"></div>

                    <div className="referral-controls">
                        <div className="tab-control" ref={tabContainerRef}>
                            <div
                                className="tab-highlight"
                                style={{left: tabHighlightStyle.left, width: tabHighlightStyle.width}}
                            />
                            <div
                                className={`tab-option ${linkType === 'tg' ? 'active' : ''}`}
                                ref={tabRefs.tg}
                                onClick={() => handleLinkTypeChange('tg')}
                            >
                                <span>Telegram</span>
                            </div>
                            <div
                                className={`tab-option ${linkType === 'web' ? 'active' : ''}`}
                                ref={tabRefs.web}
                            >
                                <span>Web</span>
                            </div>
                        </div>

                        <div className="referral-link-field">
                            <span className="link-text">{referralLink || 'Генерация ссылки...'}</span>
                            <button className="copy-button" onClick={handleCopyLink}>
                                <img src={`${ASSET_PATH}/0_110.svg`} alt="Copy"/>
                            </button>
                        </div>

                        <div className="action-buttons-row">
                            <button className="action-button-small" onClick={handleShare}>
                                <img src={`${ASSET_PATH}/0_118.svg`} alt="Share"/>
                                <span>Share</span>
                            </button>
                            <button className="action-button-small" onClick={handleQR}>
                                <img src={`${ASSET_PATH}/0_124.svg`} alt="QR"/>
                                <span>QR</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Portal>
            {/* ✅ ВСЕ SHEET КОМПОНЕНТЫ СНАРУЖИ - НЕ ВНУТРИ SKROLL-КОНТЕЙНЕРА */}
            <ReferralListSheet
                isVisible={showReferralList}
                onClose={() => setShowReferralList(false)}
                referrals={referrals}
                totalEarnings={stats.availableBalance}
            />
                </Portal>
            <Portal>
            <QRCodeSheet
                isVisible={showQRCode}
                onClose={handleCloseQR}
                qrData={referralLink}
                title="Реферальная ссылка"
            />
            </Portal>
            <Portal>
            <WithdrawalSheet
                isVisible={showWithdrawalSheet}
                onClose={() => setShowWithdrawalSheet(false)}
                onConfirm={handleWithdrawalConfirm}
                amount={stats.availableBalance}
            />
            </Portal>
            <Portal>
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
                duration={3000}
            />
            </Portal>
        </div>
    );
            }
